import { useEffect, useState } from "react";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Input, NativeSelect } from "@ncp/ui";
import type { ConnectorSecretOption } from "./api";

type Pair = [string, string];
const pairsOf = (value: Record<string, string> | undefined): Pair[] => Object.entries(value ?? {}).map(([k, v]) => [k, v ?? ""]);
const objectOf = (rows: Pair[]) => Object.fromEntries(rows.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v]));

/** Name/value rows (query values, headers, response mapping); empty names are dropped when saved. */
export function KeyValueRows({ value, onChange, readOnly, keyPlaceholder, valuePlaceholder, addLabel }: {
  value: Record<string, string> | undefined; onChange: (next: Record<string, string>) => void; readOnly: boolean; keyPlaceholder?: string; valuePlaceholder?: string; addLabel?: string;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Pair[]>(() => pairsOf(value));
  const external = JSON.stringify(value ?? {});
  useEffect(() => {
    if (JSON.stringify(objectOf(rows)) !== external) setRows(pairsOf(JSON.parse(external) as Record<string, string>));
  }, [external]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (next: Pair[]) => { setRows(next); onChange(objectOf(next)); };
  return (
    <div className="space-y-1.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={k} dir="ltr" className="h-9 w-44 shrink-0 font-mono text-xs" placeholder={keyPlaceholder ?? t("admin:connectors.fields.name")} readOnly={readOnly}
            onChange={(e) => update(rows.map((r, j): Pair => (j === i ? [e.target.value, r[1]] : r)))} />
          <Input value={v} dir="ltr" className="h-9 min-w-0 flex-1 font-mono text-xs" placeholder={valuePlaceholder ?? t("admin:connectors.fields.value")} readOnly={readOnly}
            onChange={(e) => update(rows.map((r, j): Pair => (j === i ? [r[0], e.target.value] : r)))} />
          {!readOnly ? <Button size="icon-sm" variant="ghost" className="text-danger-600" aria-label={t("admin:common.remove")} onClick={() => update(rows.filter((_, j) => j !== i))}><Trash2 /></Button> : null}
        </div>
      ))}
      {rows.length === 0 && readOnly ? <p className="text-xs text-muted-foreground">—</p> : null}
      {!readOnly ? <Button size="sm" variant="ghost" onClick={() => setRows([...rows, ["", ""]])}><Plus /> {addLabel ?? t("admin:connectors.fields.addRow")}</Button> : null}
    </div>
  );
}

const CUSTOM = "__custom";

/** A `secret://key` reference picked from Settings → Secrets; the value itself never appears in a definition. */
export function SecretField({ value, onChange, secrets, readOnly }: { value: string; onChange: (v: string) => void; secrets: ConnectorSecretOption[]; readOnly: boolean }) {
  const { t } = useTranslation();
  const known = secrets.find((s) => s.reference === value);
  const [custom, setCustom] = useState(!!value && !known);
  const notReference = !!value && !value.toLowerCase().startsWith("secret://");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <KeyRound className="size-4 shrink-0 text-muted-foreground" />
        <NativeSelect value={custom ? CUSTOM : value} disabled={readOnly} className="h-9 min-w-0 flex-1"
          onChange={(e) => { if (e.target.value === CUSTOM) { setCustom(true); if (!value) onChange("secret://"); } else { setCustom(false); onChange(e.target.value); } }}>
          <option value="">—</option>
          {secrets.map((s) => <option key={s.key} value={s.reference}>{s.key}{s.hasValue ? "" : ` · ${t("admin:connectors.auth.noValue")}`}</option>)}
          <option value={CUSTOM}>{t("admin:connectors.auth.otherSecret")}</option>
        </NativeSelect>
        {known ? <Badge variant={known.hasValue ? "success" : "warning"} size="sm">{known.hasValue ? t("admin:connectors.auth.hasValue") : t("admin:connectors.auth.noValue")}</Badge> : null}
      </div>
      {custom ? <Input value={value} dir="ltr" className="h-9 font-mono text-xs" placeholder="secret://partner-api-key" readOnly={readOnly} onChange={(e) => onChange(e.target.value.trim())} /> : null}
      {notReference ? <p className="text-[11px] text-danger-700">{t("admin:connectors.auth.mustBeReference")}</p> : null}
      {known?.description ? <p className="text-[11px] text-muted-foreground">{known.description}</p> : null}
    </div>
  );
}
